import React from 'react';
import { Text, Button, makeStyles, shorthands, tokens, mergeClasses } from '@fluentui/react-components';
import { Board20Regular, ArrowRight16Regular } from '@fluentui/react-icons';
import type { KeyQuestion } from '../../types/discovery';
import { MutationButton, useWorkspaceAccess } from '../security/WorkspaceAccess';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    transitionProperty: 'box-shadow, border-color, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
    ...shorthands.padding('12px', '14px'),
    gap: '6px',
    ':hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      transform: 'translateY(-1px)',
    },
  },
  cardBest: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    boxShadow: '0 2px 8px rgba(0,120,212,0.10)',
    ':hover': {
      boxShadow: '0 6px 20px rgba(0,120,212,0.14)',
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    lineHeight: '20px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bestBadge: {
    fontSize: '10px',
    fontWeight: 600,
    color: tokens.colorPaletteMarigoldForeground2,
    whiteSpace: 'nowrap',
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '12px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground3,
  },
  multiBadge: {
    fontSize: '9px',
    fontWeight: 600,
    color: tokens.colorPaletteBerryForeground1,
    backgroundColor: tokens.colorPaletteBerryBackground2,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('1px', '5px'),
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  description: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '16px',
  },
  kpiRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  kpiPill: {
    fontSize: '9px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('6px'),
    ...shorthands.padding('1px', '7px'),
  },
  questionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  questionsLabel: {
    fontSize: '9px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: tokens.colorNeutralForeground4,
  },
  questionItem: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '15px',
    '::before': {
      content: '"•  "',
      color: tokens.colorNeutralForeground4,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '4px',
    marginTop: 'auto',
  },
  exploreLink: {
    fontSize: '11px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
});

interface Props {
  question: KeyQuestion;
  onCreateDashboard: (viewId: string, prompt: string) => void;
  onExplore: (viewId: string) => void;
}

const KeyQuestionCard: React.FC<Props> = ({
  question,
  onCreateDashboard,
  onExplore,
}) => {
  const styles = useStyles();
  const { runMutation } = useWorkspaceAccess();
  const isBest = question.rank === 1;

  return (
    <div className={mergeClasses(styles.card, isBest && styles.cardBest)}>
      <div className={styles.titleRow}>
        <Text className={styles.title}>{question.title}</Text>
        {isBest && <span className={styles.bestBadge}>⭐ Best Start</span>}
      </div>
      <div className={styles.subtitle}>
        {question.isCrossDomain && <span className={styles.multiBadge}>Cross-Domain</span>}
        {question.businessContextLabel !== 'Business Analytics' && (
          <span>{question.businessContextLabel}</span>
        )}
      </div>
      {question.description && (
        <Text className={styles.description}>{question.description}</Text>
      )}
      {question.relatedMetrics.length > 0 && (
        <div className={styles.kpiRow}>
          {question.relatedMetrics.slice(0, 3).map((m) => (
            <span key={m} className={styles.kpiPill}>{m}</span>
          ))}
        </div>
      )}
      {question.questions.length > 0 && (
        <div className={styles.questionsSection}>
          <Text className={styles.questionsLabel}>Key Questions</Text>
          {question.questions.slice(0, 3).map((q, i) => (
            <Text key={i} className={styles.questionItem}>{q}</Text>
          ))}
        </div>
      )}
      <div className={styles.actions}>
        <MutationButton
          size="small"
          appearance="primary"
          icon={<Board20Regular />}
          onClick={() => runMutation(() => onCreateDashboard(question.primaryViewId, question.dashboardPrompt))}
        >
          Create Dashboard
        </MutationButton>
        <Button
          appearance="transparent"
          size="small"
          icon={<ArrowRight16Regular />}
          iconPosition="after"
          onClick={() => onExplore(question.primaryViewId)}
          className={styles.exploreLink}
        >
          Explore
        </Button>
      </div>
    </div>
  );
};

export default KeyQuestionCard;